import { Address } from '@adornis/address/db/address.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { validate } from '@adornis/validation/decorators.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { UserStammdaten } from '../../entities/stammdaten.js';
import type { RegistrationStammdatenPlugin } from '../plugin-types.js';

export const tudaRegistrationStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::tuda',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new TudaStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  getFields: () => {
    return Object.keys(TudaStammdaten.fields).filter(it => !['_id', '_class', 'userID', 'institutionID'].includes(it));
  },
  classType: () => TudaStammdaten,
};

export enum TudaGenderEnum {
  TUDA_MALE = 'TUDA_MALE',
  TUDA_FEMALE = 'TUDA_FEMALE',
  TUDA_DIVERSE = 'TUDA_DIVERSE',
  TUDA_NOT_SPECIFIED = 'TUDA_NOT_SPECIFIED',
}
registerEnum({ enum: TudaGenderEnum, name: 'TudaGenderEnum' });

export enum TudaUniEnum {
  TUDA_FRA = 'TUDA_FRA',
  TUDA_GU = 'TUDA_GU',
  TUDA_HDA = 'TUDA_HDA',
  TUDA_HSRM = 'TUDA_HSRM',
  TUDA_TUDA = 'TUDA_TUDA',
  TUDA_JGU = 'TUDA_JGU',
  TUDA_ANDERE = 'TUDA_ANDERE',
}
registerEnum({ enum: TudaUniEnum, name: 'TudaUniEnum' });

export enum TudaDepartmentEnum {
  TUDA_ARGICULTURE = 'TUDA_ARGICULTURE',
  TUDA_ARCHITECTURE = 'TUDA_ARCHITECTURE',
  TUDA_ARTS = 'TUDA_ARTS',
  TUDA_CIVIL_ENGINEERING = 'TUDA_CIVIL_ENGINEERING',
  TUDA_COMPUTER_SCIENCE = 'TUDA_COMPUTER_SCIENCE',
  TUDA_ECONOMICS_MANAGEMENT = 'TUDA_ECONOMICS_MANAGEMENT',
  TUDA_ELECTRICAL_ENGINEERING = 'TUDA_ELECTRICAL_ENGINEERING',
  TUDA_ENGINEERING_TECHNOLOGY = 'TUDA_ENGINEERING_TECHNOLOGY',
  TUDA_EVENT_MANAGEMENT = 'TUDA_EVENT_MANAGEMENT',
  TUDA_HEALTH = 'TUDA_HEALTH',
  TUDA_LAW = 'TUDA_LAW',
  TUDA_LINGUISTICS = 'TUDA_LINGUISTICS',
  TUDA_MECHANICAL_ENGINEERING = 'TUDA_MECHANICAL_ENGINEERING',
  TUDA_MARKETING = 'TUDA_MARKETING',
  TUDA_MATHEMATICS = 'TUDA_MATHEMATICS',
  TUDA_PSYCHOLOGY = 'TUDA_PSYCHOLOGY',
  TUDA_SOCIAL_SCIENCE = 'TUDA_SOCIAL_SCIENCE',
  TUDA_SPORTS = 'TUDA_SPORTS',
  TUDA_PEDAGOGY = 'TUDA_PEDAGOGY',
  TUDA_OTHER = 'TUDA_OTHER',
}
registerEnum({ enum: TudaDepartmentEnum, name: 'TudaDepartmentEnum' });

export enum TudaDesiredDegreeEnum {
  TUDA_BACHELOR = 'TUDA_BACHELOR',
  TUDA_MASTER = 'TUDA_MASTER',
  TUDA_STAATSEXAMEN_LEHRAMT = 'TUDA_STAATSEXAMEN_LEHRAMT',
  TUDA_STAATSEXAMEN_MEDIZIN_PHARMAZIE = 'TUDA_STAATSEXAMEN_MEDIZIN_PHARMAZIE',
  TUDA_STAATSEXAMEN_JURA = 'TUDA_STAATSEXAMEN_JURA',
  TUDA_PROMOTION = 'TUDA_PROMOTION',
  TUDA_SONSTIGE = 'TUDA_SONSTIGE',
}
registerEnum({ enum: TudaDesiredDegreeEnum, name: 'TudaDesiredDegreeEnum' });

export enum TudaMentorExchangeFrequencyEnum {
  TUDA_MINDESTENS_1_MAL_PRO_WOCHE = 'TUDA_MINDESTENS_1_MAL_PRO_WOCHE',
  TUDA_ZWEI_MAL_PRO_MONAT = 'TUDA_ZWEI_MAL_PRO_MONAT',
  TUDA_SELTENER_ALS_ZWEIMAL_PRO_MONAT = 'TUDA_SELTENER_ALS_ZWEIMAL_PRO_MONAT',
}
registerEnum({ enum: TudaMentorExchangeFrequencyEnum, name: 'TudaMentorExchangeFrequencyEnum' });

export enum TudaMentorExchangeMethodEnum {
  TUDA_ONLINE_DIGITAL = 'TUDA_ONLINE_DIGITAL',
  TUDA_IN_PRAESENZ = 'TUDA_IN_PRAESENZ',
  TUDA_BEIDES = 'TUDA_BEIDES',
}
registerEnum({ enum: TudaMentorExchangeMethodEnum, name: 'TudaMentorExchangeMethodEnum' });

export enum TudaMentorExchangeLanguageEnum {
  TUDA_DEUTSCH = 'TUDA_DEUTSCH',
  TUDA_ENGLISCH = 'TUDA_ENGLISCH',
  TUDA_BEIDES = 'TUDA_BEIDES',
}
registerEnum({ enum: TudaMentorExchangeLanguageEnum, name: 'TudaMentorExchangeLanguageEnum' });

export enum TudaMentorMatchGenderEnum {
  TUDA_MATCH_MALE = 'TUDA_MATCH_MALE',
  TUDA_MATCH_FEMALE = 'TUDA_MATCH_FEMALE',
  TUDA_MATCH_BOTH = 'TUDA_MATCH_BOTH',
}
registerEnum({ enum: TudaMentorMatchGenderEnum, name: 'TudaMentorMatchGenderEnum' });

export enum TudaMentoringTopicEnum {
  TUDA_KONTAKTE_KNUPFEN = 'TUDA_KONTAKTE_KNUPFEN', // Kontakte knüpfen
  TUDA_BRANCHEN_KENNENLERNEN = 'TUDA_BRANCHEN_KENNENLERNEN', // Branchen und Tätigkeitsfelder kennenlernen
  TUDA_ARBEITSMOEGLICHKEITEN_ERKUNDEN = 'TUDA_ARBEITSMOEGLICHKEITEN_ERKUNDEN', // Arbeitsmöglichkeiten im Rhein-Main-Gebiet erkunden
  TUDA_UNTERSTUEZUNG_DER_KARIEREPLANUNG = 'TUDA_UNTERSTUEZUNG_DER_KARIEREPLANUNG', // Unterstützung bei der Karriereplanung und -gestaltung
  TUDA_EINBLICK_ARBEITSALLTAG = 'TUDA_EINBLICK_ARBEITSALLTAG', // Einblick in Arbeitsalltag erhalten
  TUDA_FACHLICHE_FRAGEN = 'TUDA_FACHLICHE_FRAGEN', // Fachliche Fragen klären
  TUDA_WORK_LIFE_BALANCE = 'TUDA_WORK_LIFE_BALANCE', // Vereinbarkeit von Familie und Beruf „Work-Life-Balance“
  TUDA_FRAUEN_KARRIEREPLANUNG = 'TUDA_FRAUEN_KARRIEREPLANUNG', // Frauen Karriereplanung
  TUDA_KOMMUNIKATIONS_TRAINING = 'TUDA_KOMMUNIKATIONS_TRAINING', // Training im Bereich Kommunikationsfähigkeiten und Selbstvermarktung
  TUDA_BEWERBUNGSPROZESS_UNTERTUETZUNG = 'TUDA_BEWERBUNGSPROZESS_UNTERTUETZUNG', // Unterstützung im Bewerbungsprozess (Bewerbungsunterlagen/ Interview)
  TUDA_INTROSPEKTIVE = 'TUDA_INTROSPEKTIVE', // Persönliche Stärken und Schwächen erkennen
  TUDA_ALLEINSTELLUNGSMERKMAL_FINDEN = 'TUDA_ALLEINSTELLUNGSMERKMAL_FINDEN', // Alleinstellungsmerkmal/ USP herausarbeiten
  TUDA_WEITERBILDUNGSMOEGLICHKEITEN = 'TUDA_WEITERBILDUNGSMOEGLICHKEITEN', // Tipps zu Weiterbildungsmöglichkeiten
  TUDA_ARBEITSTREFFEN_BEGLEITEN = 'TUDA_ARBEITSTREFFEN_BEGLEITEN', // Mentor/ Mentorin zu Arbeitstreffen begleiten (Meetings, Messen, etc.)
  TUDA_DEUTSCH_LERNEN = 'TUDA_DEUTSCH_LERNEN', // Deutsch lernen/ verbessern
  TUDA_ANDERE = 'TUDA_ANDERE', // Andere:
}
registerEnum({ enum: TudaMentoringTopicEnum, name: 'MentoringTopicEnum' });

@Entity()
export class TudaStammdaten extends UserStammdaten {
  static override _class = 'TudaStammdaten';

  override getTranslationNamespace() {
    return 'tuda';
  }

  // 1. Persönliche Angaben
  // Geschlecht: männlich/ weiblich/ divers/ ohne Angabe
  @validate(nonOptional())
  @Field(type => TudaGenderEnum)
  gender: Maybe<TudaGenderEnum>;

  @validate(nonOptional())
  @Field(type => DateTime)
  dateOfBirth: Maybe<DateTime>;

  // Aktueller Wohnort:
  @validate(({ value, target, key }) => {
    const errorFields = (
      !target.address
        ? [{ path: ['address'], target }]
        : [
            ...(!target.address.zipCity
              ? [{ path: ['address', 'zipCity'], target: target.address }]
              : [
                  !target.address?.zipCity?.zip
                    ? { path: ['address', 'zipCity', 'zip'], target: target.address?.zipCity }
                    : null,
                  !target.address?.zipCity?.city
                    ? { path: ['address', 'zipCity', 'city'], target: target.address?.zipCity }
                    : null,
                ]),
          ]
    ).filter(Boolean) as Array<{ path: string[]; target: any }>;

    if (errorFields.length)
      throw new MultipleValidationError(
        errorFields.map(
          ({ path, target }) =>
            new ValidationError(`Key ${path.slice(-1)[0]} is required!`, {
              key: path.slice(-1)[0],
              translationKey: 'validation_non_optional',
              path: path.slice(0, -1),
              target,
            }),
        ),
      );
  })
  @Field(type => Address, { default: v => v ?? new Address({}) })
  address: Maybe<Address>;

  // Staatsangehörigkeit: (Drop Down alle Länder)
  @validate(nonOptional())
  @Field(type => String)
  nationality: Maybe<string>;

  // Geburtsland: (Drop Down alle Länder)
  @validate(nonOptional())
  @Field(type => String)
  countryOfBirth: Maybe<string>;

  // Ich habe eine Zuwanderungsgeschichte (Mindestens ein Elternteil und/oder ich selbst wurde außerhalb Deutschlands geboren): ja / nein
  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { dynamicValidator: 'non_optional', default: value => value ?? false })
  isImmigrant: Maybe<boolean>;

  @validate(nonOptional())
  @Field(type => String)
  phoneNumber: Maybe<string>;

  // 2. Studium
  @validate(nonOptional())
  @Field(type => TudaUniEnum)
  currentUni: Maybe<TudaUniEnum>;

  // Fachsemester:
  @validate(nonOptional())
  @Field(type => String)
  semester: Maybe<string>;

  // Studiengang:
  @validate(nonOptional())
  @Field(type => String)
  courseOfStudy: Maybe<string>;

  // Fachbereich: (Liste wie aktuell)
  @validate(nonOptional())
  @Field(type => TudaDepartmentEnum)
  department: Maybe<TudaDepartmentEnum>;

  // Angestrebter Abschluss:
  @validate(nonOptional())
  @Field(type => TudaDesiredDegreeEnum)
  desiredDegree: Maybe<TudaDesiredDegreeEnum>;

  // 3. Herausforderungen und Motivation
  // Welche Schwierigkeiten hast du bei der Jobsuche?
  @validate(nonOptional())
  @Field(type => String)
  jobHuntingDifficulties: Maybe<string>;

  // Warum möchtest du am Career Mentoring-Programm teilnehmen?
  @validate(nonOptional())
  @Field(type => String)
  participationReason: Maybe<string>;

  // Welche Fragen möchtest du mit deinem Mentor/ deiner Mentorin besprechen?
  @validate(nonOptional())
  @Field(type => String)
  questions: Maybe<string>;

  // 4. Wünsche an das Programm
  // Wie oft möchtest du dich mit deinem Mentor/ deiner Mentorin austauschen?
  @validate(nonOptional())
  @Field(type => TudaMentorExchangeFrequencyEnum)
  mentorExchangeFrequency: Maybe<TudaMentorExchangeFrequencyEnum>;

  // Wie soll der Austausch stattfinden?
  @validate(nonOptional())
  @Field(type => TudaMentorExchangeMethodEnum)
  mentorExchangeMethod: Maybe<TudaMentorExchangeMethodEnum>;

  // In welcher Sprache möchtest du mit deinem Mentor/ deiner Mentorin sprechen?
  @validate(nonOptional())
  @Field(type => TudaMentorExchangeLanguageEnum)
  mentorExchangeLanguage: Maybe<TudaMentorExchangeLanguageEnum>;

  // Mit wem möchtest du gematcht werden?
  @validate(nonOptional())
  @Field(type => TudaMentorMatchGenderEnum)
  mentorMatchGender: Maybe<TudaMentorMatchGenderEnum>;

  // Welche Themen sind dir im Mentoring besonders wichtig? Bitte kreuze an. (max. 3 auswählen)
  @validate(({ value, target, key }) => {
    nonOptional()({ value, target, key });
    if (value.length > 3)
      throw new ValidationError('To many Topics selected. Maximum is 3', {
        key: 'tuda_to_many_topics',
        translationKey: 'tuda_to_many_topics',
      });
  })
  @Field(type => [TudaMentoringTopicEnum])
  mentoringTopic: Maybe<[TudaMentoringTopicEnum]>;

  @Field(type => String)
  individualizedTopic: Maybe<string>;

  // Folgendes möchte ich noch mitteilen:
  @Field(type => String)
  additionalInformation: Maybe<string>;
}
